import _ from 'lodash';

/**
 * Shared vitals vs labs classification for the CDA sidebar and historical Labs page.
 * Uses LA_labccd.ZV_labccd_vitalsign (login/watch.vitalSignCodes).
 */

/** Parse vital-sign CCD list into a Set for lookups. */
export function parseVitalCcdSet(vitalSignCodes) {
  return new Set((vitalSignCodes || []).map((c) => parseInt(c, 10)));
}

/** True once the vital-sign catalog has loaded. */
export function hasVitalCatalog(vitalSignCodes) {
  return parseVitalCcdSet(vitalSignCodes).size > 0;
}

/** CDA sidebar: until the vital catalog loads, treat measures as vitals. */
export function isVitalCcdForCda(ccdId, vitalSignCodes) {
  const vitalSet = parseVitalCcdSet(vitalSignCodes);
  if (vitalSet.size === 0) return true;
  return vitalSet.has(parseInt(ccdId, 10));
}

/**
 * CDA sidebar, for one measure: the measure's own flag when it carries one (the
 * port's staged results say whether their concept is a vital sign,
 * `isVitalSign`), else its CCD against the vital-sign catalog as above.
 */
export function isVitalMeasureForCda(meas, vitalSignCodes) {
  if (meas && typeof meas.isVitalSign === 'boolean') return meas.isVitalSign;
  return isVitalCcdForCda(meas && meas.ccd, vitalSignCodes);
}

/** True when the measure CCD is a non-vital (lab) analyte. */
export function isLabCcd(ccdId, vitalSignCodes) {
  if (!hasVitalCatalog(vitalSignCodes)) return false;
  return !parseVitalCcdSet(vitalSignCodes).has(parseInt(ccdId, 10));
}

/** Office-visit procedures keep vitals nested; labs are split out separately. */
export function isOfficeVisitProcedure(procCcd, ccds) {
  return !!(procCcd && ccds && ccds[procCcd] && ccds[procCcd].officevisit);
}

/** EKG/ECG rows have measures but stay under Procedures, not Labs. */
export function isEkgProcedure(procCcd, ccds) {
  const desc = procCcd && ccds && ccds[procCcd] && ccds[procCcd].desc;
  if (!desc) return false;
  return /electrocardiogram|\bekg\b|\becg\b/i.test(desc);
}

/**
 * BMP/CMP-style organizers with child measures are lab panels.
 * Office visits, EKGs, and measure-less rows are excluded.
 */
export function isLabPanelProcedure(procCcd, ccds, measIds) {
  return (measIds || []).length > 0
    && !isOfficeVisitProcedure(procCcd, ccds)
    && !isEkgProcedure(procCcd, ccds);
}

/** True when a historical procedure-kind group is a lab panel (for Labs vs Procedures pages). */
export function procedureKindIsLabPanel(kindGroup, getMeasuresForProcId, ccds) {
  for (const proc of kindGroup.procedures || []) {
    const procCcd = proc.codes && proc.codes[0];
    for (const procId of proc.ids || []) {
      const measList = getMeasuresForProcId(procId) || [];
      if (isLabPanelProcedure(procCcd, ccds, measList.map((m) => m.id))) {
        return true;
      }
    }
  }
  return false;
}

/** Best YYYY-MM-DD date on a measure or procedure record. */
export function effectiveRecordDate(record, fallbackLo) {
  if (!record) {
    return fallbackLo || 'Undated';
  }
  const candidates = [record.lodate, record.lo, record.lotimestamp, record.hidate, record.hi];
  for (const raw of candidates) {
    if (raw === null || raw === undefined || raw === '') continue;
    const match = String(raw).match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
  }
  return fallbackLo || 'Undated';
}

/** Collection date for a lab-panel organizer, avoiding encounter/document fallback dates. */
function panelEffectiveDate(procedureMeta, measIds, measures, fallbackLo) {
  const procDate = effectiveRecordDate(procedureMeta, null);
  const fallbackDate = effectiveRecordDate({ lo: fallbackLo }, null) || fallbackLo || 'Undated';

  if (procDate && procDate !== 'Undated' && procDate !== fallbackDate) {
    return procDate;
  }

  for (const measId of measIds || []) {
    const measDate = effectiveRecordDate(measures[measId], null);
    if (measDate && measDate !== 'Undated' && measDate !== fallbackDate) {
      return measDate;
    }
  }

  if (procDate && procDate !== 'Undated') return procDate;
  return fallbackLo || 'Undated';
}

/** Look up a linked lab-panel organizer date for a measure (via procmeas). */
function findLabPanelDateForMeasure(measId, procmeas, procedures, measures, ccds) {
  for (const [procId, measIds] of Object.entries(procmeas || {})) {
    if (!(measIds || []).some((id) => String(id) === String(measId))) continue;
    const proc = procedures[procId];
    if (!proc) continue;
    if (!isLabPanelProcedure(proc.ccd, ccds, measIds)) continue;
    const panelDate = panelEffectiveDate(proc, measIds, measures, null);
    if (panelDate && panelDate !== 'Undated') return panelDate;
  }
  return null;
}

/**
 * Sidebar bucket date for a lab measure.
 * Prefers collection date; falls back to the parent panel when the measure
 * inherited an encounter/document date.
 */
export function resolveMeasureSidebarDate(meas, measId, {
  procmeas,
  procedures,
  measures,
  ccds,
  fallbackLo,
}) {
  const measDate = effectiveRecordDate(meas, null);
  const panelDate = findLabPanelDateForMeasure(measId, procmeas, procedures, measures, ccds);
  const fallbackDate = effectiveRecordDate({ lo: fallbackLo }, null) || fallbackLo || 'Undated';

  if (panelDate && panelDate !== 'Undated') {
    if (!measDate || measDate === 'Undated' || measDate === fallbackDate) {
      return panelDate;
    }
  }
  if (measDate && measDate !== 'Undated') return measDate;
  if (panelDate && panelDate !== 'Undated') return panelDate;
  return fallbackDate;
}

/** Kind-and-date sidebar: labs bucket is { [kind]: { [date]: entries[] } }. */
function promoteLabMeasureToBucket(sideBarContent, bucketKind, lo, entry) {
  if (!sideBarContent[bucketKind]) {
    sideBarContent[bucketKind] = {};
  }
  if (!sideBarContent[bucketKind][lo]) {
    sideBarContent[bucketKind][lo] = [];
  }
  sideBarContent[bucketKind][lo].push(entry);
}

/** Date-and-kind sidebar: labs bucket is { [date]: { [kind]: entries[] } }. */
function promoteFlatMeasureToBucket(sideBarContent, dateLo, bucketKind, entry) {
  if (!sideBarContent[dateLo]) {
    sideBarContent[dateLo] = {};
  }
  if (!sideBarContent[dateLo][bucketKind]) {
    sideBarContent[dateLo][bucketKind] = [];
  }
  sideBarContent[dateLo][bucketKind].push(entry);
}

function orderLabBucketEntries(sideBarContent, bucketKind, lo) {
  if (sideBarContent[bucketKind] && sideBarContent[bucketKind][lo]) {
    sideBarContent[bucketKind][lo] = _.orderBy(sideBarContent[bucketKind][lo], 'weight', 'desc');
  }
}

function buildLabMeasureEntry(meas, measId, measLo, { procCcd, measureKind, ccds }) {
  return {
    ...meas,
    ...(measLo != null ? { lo: measLo } : {}),
    dname: (ccds[meas.ccd] || {}).desc || String(meas.ccd),
    kind: measureKind,
    procCcd,
    id: measId,
    weight: meas.weight ? meas.weight : 0,
  };
}

function buildPanelMeasureEntries(measIds, measures, procCcd, measureKind, ccds) {
  const panelMeasures = [];
  (measIds || []).forEach((measId) => {
    const meas = measures[measId];
    if (!meas) return;
    panelMeasures.push(buildLabMeasureEntry(meas, measId, null, { procCcd, measureKind, ccds }));
  });
  return panelMeasures;
}

function promoteLabPanelToBucket(sideBarContent, bucketKind, lo, panelEntry, flatBuckets) {
  if (flatBuckets) {
    promoteFlatMeasureToBucket(sideBarContent, lo, bucketKind, panelEntry);
  } else {
    promoteLabMeasureToBucket(sideBarContent, bucketKind, lo, panelEntry);
  }
}

/**
 * Office-visit procedures: move lab children to the Labs bucket, keep vitals nested.
 */
export function splitProcedureMeasuresForSidebar({
  measIds,
  measures,
  procCcd,
  measureKind,
  lo,
  ccds,
  vitalSignCodes,
  labKind,
  sideBarContent,
  flatBuckets = false,
  procmeas = {},
  procedures = {},
}) {
  const vitalMeasures = [];
  let movedLabCount = 0;
  const catalogLoaded = hasVitalCatalog(vitalSignCodes);

  (measIds || []).forEach((measId) => {
    const meas = measures[measId];
    if (!meas) return;

    const enriched = buildLabMeasureEntry(meas, measId, null, { procCcd, measureKind, ccds });

    if (catalogLoaded && isLabCcd(meas.ccd, vitalSignCodes)) {
      const measLo = resolveMeasureSidebarDate(meas, measId, {
        procmeas,
        procedures,
        measures,
        ccds,
        fallbackLo: lo,
      });
      const entry = buildLabMeasureEntry(meas, measId, measLo, { procCcd, measureKind, ccds });
      if (flatBuckets) {
        promoteFlatMeasureToBucket(sideBarContent, measLo, labKind, entry);
      } else {
        promoteLabMeasureToBucket(sideBarContent, labKind, measLo, entry);
        orderLabBucketEntries(sideBarContent, labKind, measLo);
      }
      movedLabCount += 1;
    } else {
      vitalMeasures.push(enriched);
    }
  });

  return { vitalMeasures, movedLabCount };
}

/**
 * Lab panels (BMP, CMP, etc.): move the whole procedure into Labs with nested measures.
 * Returns hideProcedure: true so the shell is removed from Procedures.
 */
export function moveLabPanelToSidebar({
  measIds,
  measures,
  procCcd,
  procKind,
  measureKind,
  lo,
  ccds,
  labKind,
  sideBarContent,
  flatBuckets = false,
  procedureMeta = {},
  cdaIndex = null,
}) {
  const panelMeasures = buildPanelMeasureEntries(measIds, measures, procCcd, measureKind, ccds);
  if (panelMeasures.length === 0) {
    return { vitalMeasures: [], hideProcedure: false };
  }

  const panelLo = panelEffectiveDate(procedureMeta, measIds, measures, lo);
  const entry = {
    ...procedureMeta,
    id: procedureMeta.id,
    cdaIndex,
    ccd: procCcd,
    desc: (ccds[procCcd] || {}).desc || String(procCcd),
    kind: procKind,
    lo: panelLo,
    measures: panelMeasures,
  };

  promoteLabPanelToBucket(sideBarContent, labKind, panelLo, entry, flatBuckets);

  return { vitalMeasures: [], hideProcedure: true };
}

/**
 * Route procedure-nested measures for CDA sidebar bucketing:
 * EKG → stay in Procedures; lab panel → Labs as accordion; office visit → split vitals/labs.
 */
export function resolveProcedureMeasuresForSidebar({
  measIds,
  measures,
  procCcd,
  procKind,
  measureKind,
  lo,
  ccds,
  vitalSignCodes,
  labKind,
  sideBarContent,
  flatBuckets = false,
  procedureMeta = {},
  cdaIndex = null,
  procmeas = {},
  procedures = {},
}) {
  if (isEkgProcedure(procCcd, ccds)) {
    return {
      vitalMeasures: buildPanelMeasureEntries(measIds, measures, procCcd, measureKind, ccds),
      movedLabCount: 0,
      hideProcedure: false,
    };
  }
  if (isLabPanelProcedure(procCcd, ccds, measIds)) {
    return moveLabPanelToSidebar({
      measIds,
      measures,
      procCcd,
      procKind,
      measureKind,
      lo,
      ccds,
      labKind,
      sideBarContent,
      flatBuckets,
      procedureMeta,
      cdaIndex,
    });
  }
  const split = splitProcedureMeasuresForSidebar({
    measIds,
    measures,
    procCcd,
    measureKind,
    lo,
    ccds,
    vitalSignCodes,
    labKind,
    sideBarContent,
    flatBuckets,
    procmeas,
    procedures,
  });
  const hadMeasures = (measIds || []).length > 0;
  const hideProcedure = hadMeasures && split.vitalMeasures.length === 0 && split.movedLabCount > 0;
  return { ...split, hideProcedure };
}
